import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { GradientText } from '../GradientText'

export type PromoBannerSize = 'default' | 'compact'

export interface PromoBannerProps extends Omit<BoxProps, 'children' | 'title'> {
  eyebrow: React.ReactNode
  title: React.ReactNode
  description: React.ReactNode
  ctaLabel: React.ReactNode
  ctaIcon?: React.ReactNode
  href?: string
  onCtaClick?: () => void
  size?: PromoBannerSize
}

const Root = styled(Box, {
  shouldForwardProp: (p) => p !== 'size',
})<{ size: PromoBannerSize }>(({ size }) => ({
  position: 'relative',
  borderRadius: 'var(--ds-radius-xl)',
  overflow: 'hidden',
  background: 'var(--ds-night-canvas)',
  color: 'var(--ds-night-ink)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      size === 'compact'
        ? 'radial-gradient(ellipse 55% 80% at 80% 30%, rgba(190,157,253,0.2), transparent 62%)'
        : 'radial-gradient(ellipse 60% 80% at 82% 30%, rgba(190,157,253,0.22), transparent 60%), radial-gradient(ellipse 45% 60% at 12% 90%, rgba(255,207,125,0.14), transparent 60%)',
  },
}))

const Inner = styled(Box, {
  shouldForwardProp: (p) => p !== 'size',
})<{ size: PromoBannerSize }>(({ size }) => ({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: size === 'compact' ? 24 : 30,
  padding: size === 'compact' ? '34px 38px' : '40px 44px',
}))

const Title = styled('h2', {
  shouldForwardProp: (p) => p !== 'size',
})<{ size: PromoBannerSize }>(({ size }) => ({
  margin: '10px 0 0',
  fontSize: size === 'compact' ? 23 : 26,
  fontWeight: 720,
  letterSpacing: '-0.03em',
}))

const Description = styled('p', {
  shouldForwardProp: (p) => p !== 'size',
})<{ size: PromoBannerSize }>(({ size }) => ({
  margin: '8px 0 0',
  fontSize: size === 'compact' ? 13 : 13.5,
  color: 'var(--ds-night-ink-muted)',
  maxWidth: size === 'compact' ? 430 : 460,
}))

const Cta = styled('a')({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  flexShrink: 0,
  cursor: 'pointer',
  height: 50,
  padding: '0 28px',
  borderRadius: 'var(--ds-radius-pill)',
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
  background: 'rgba(244, 243, 246, 0.1)',
  color: 'var(--ds-night-ink)',
  boxShadow: 'inset 0 0 0 1px var(--ds-night-border)',
  backdropFilter: 'blur(10px)',
  transition: 'background var(--ds-duration-normal) var(--ds-ease)',
  '&:hover': {
    background: 'rgba(244, 243, 246, 0.16)',
  },
  '& .arr': {
    transition: 'transform var(--ds-duration-fast) var(--ds-ease)',
  },
  '&:hover .arr': {
    transform: 'translateX(3px)',
  },
})

/** Night-toned upsell band — the "turn this into a 3D gallery" / "walk the gallery" moment. */
export function PromoBanner({
  eyebrow,
  title,
  description,
  ctaLabel,
  ctaIcon = '◈',
  href,
  onCtaClick,
  size = 'default',
  ...rest
}: PromoBannerProps) {
  return (
    <Root size={size} {...rest}>
      <Inner size={size}>
        <Box>
          <GradientText variant='eyebrow'>{eyebrow}</GradientText>
          <Title size={size}>{title}</Title>
          <Description size={size}>{description}</Description>
        </Box>
        <Cta href={href} onClick={onCtaClick}>
          {ctaIcon}
          {ctaLabel}
          <span className='arr'>→</span>
        </Cta>
      </Inner>
    </Root>
  )
}
